import { defineRecipe } from '@plurall/elo-v3';

export const competitivePositionListRecipe = defineRecipe({
  className: 'competitive-position-card__list',
  base: {
    // Columns live on the list so every item (subgrid) shares the same tracks and
    // the score columns stay aligned regardless of each row's content.
    display: 'grid',
    gridTemplateColumns: '3rem minmax(12rem, 1fr) max-content max-content max-content',
    gap: 'gap.2xlarge',
    width: '100%',
    marginTop: 'padding.xlarge',
    padding: 0,
    listStyle: 'none',

    '@media (max-width: 768px)': {
      gridTemplateColumns: '3rem minmax(0, 1fr)',
    },
  },
});

export const competitivePositionEmptyRecipe = defineRecipe({
  className: 'competitive-position-card__empty',
  base: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'gap.xsmall',
    padding: 'padding.4xlarge',
    minHeight: '200px',
    height: '100%',
    textAlign: 'center',
  },
});

export const positionItemRecipe = defineRecipe({
  className: 'position-item',
  base: {
    display: 'grid',
    gridColumn: '1 / -1',
    gridTemplateColumns: 'subgrid',
    alignItems: 'center',
    gap: 'gap.2xlarge',
    width: '100%',
    minHeight: '4.75rem',
    paddingBlock: 'padding.medium',
    paddingInline: 'padding.large',
    borderWidth: 'border.width.small',
    borderStyle: 'solid',
    borderColor: 'border.tertiary',
    borderRadius: 'border.radius.large',
    background: 'background.primary',
    boxSizing: 'border-box',

    '@media (max-width: 768px)': {
      alignItems: 'flex-start',
      gap: 'gap.medium',
    },
  },
  variants: {
    tone: {
      ahead: {},
      current: {
        borderColor: 'border.feedback.success.secondary',
      },
      behind: {},
    },
  },
  defaultVariants: {
    tone: 'ahead',
  },
});

export const positionItemRankRecipe = defineRecipe({
  className: 'position-item__rank',
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '2.75rem',
    height: '2.75rem',
    borderRadius: 'border.radius.small',
    background: 'background.feedback.information.primary.alpha',
    color: 'feedback.information.stronger',
    fontSize: 'font.size.medium',
    fontWeight: 'font.weight.bold',
    flexShrink: 0,
  },
  variants: {
    tone: {
      ahead: {
        background: 'background.feedback.information.primary.alpha',
        color: 'feedback.information.stronger',
      },
      current: {
        background: 'background.feedback.success.primary.alpha',
        color: 'text.feedback.success',
      },
      behind: {
        background: 'background.feedback.warning.primary.alpha',
        color: 'text.feedback.warning',
      },
    },
  },
  defaultVariants: {
    tone: 'ahead',
  },
});

export const positionItemSchoolRecipe = defineRecipe({
  className: 'position-item__school',
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'gap.3xsmall',
    minWidth: 0,
  },
});

export const positionItemSchoolNameRecipe = defineRecipe({
  className: 'position-item__school-name',
  base: {
    margin: 0,
    color: 'text.primary',
    fontSize: 'font.size.medium',
    fontWeight: 'font.weight.bold',
    lineHeight: 'font.line.height.shorter',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',

    '@media (max-width: 768px)': {
      whiteSpace: 'normal',
    },
  },
});

export const positionItemMetaRecipe = defineRecipe({
  className: 'position-item__meta',
  base: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 'gap.xsmall',
    color: 'text.secondary',
    fontSize: 'font.size.small',
    lineHeight: 'font.line.height.shorter',
  },
});

export const positionItemMetaSeparatorRecipe = defineRecipe({
  className: 'position-item__meta-separator',
  base: {
    display: 'inline-block',
    width: '0.25rem',
    height: '0.25rem',
    borderRadius: 'border.radius.full',
    background: 'border.secondary',
  },
});

export const positionItemScoreRecipe = defineRecipe({
  className: 'position-item__score',
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'gap.3xsmall',
    minWidth: '5.75rem',

    '@media (max-width: 768px)': {
      gridColumn: '2',
      minWidth: 0,
    },
  },
});

export const positionItemScoreLabelRecipe = defineRecipe({
  className: 'position-item__score-label',
  base: {
    color: 'text.secondary',
    fontSize: 'font.size.xsmall',
    fontWeight: 'font.weight.semi.bold',
    lineHeight: 'font.line.height.shortest',
    whiteSpace: 'nowrap',
  },
});

export const positionItemScoreValueRecipe = defineRecipe({
  className: 'position-item__score-value',
  base: {
    color: 'text.primary',
    fontSize: 'font.size.large',
    fontWeight: 'font.weight.bold',
    lineHeight: 'font.line.height.shortest',
  },
});

export const positionItemAdvantageRecipe = defineRecipe({
  className: 'position-item__advantage',
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    justifySelf: 'end',
    width: 'fit-content',
    minHeight: '1.5rem',
    paddingBlock: 'padding.4xsmall',
    paddingInline: 'padding.xsmall',
    borderWidth: 'border.width.small',
    borderStyle: 'solid',
    borderColor: 'feedback.information.standard',
    borderRadius: 'border.radius.full',
    background: 'background.feedback.information.primary.alpha',
    color: 'feedback.information.stronger',
    fontSize: 'font.size.xsmall',
    fontWeight: 'font.weight.semi.bold',
    lineHeight: 'font.line.height.shortest',
    whiteSpace: 'nowrap',

    '@media (max-width: 768px)': {
      gridColumn: '2',
      justifySelf: 'start',
    },
  },
  variants: {
    tone: {
      ahead: {
        borderColor: 'feedback.information.standard',
        background: 'background.feedback.information.primary.alpha',
        color: 'feedback.information.stronger',
      },
      current: {
        borderColor: 'border.feedback.success.secondary',
        background: 'background.feedback.success.primary.alpha',
        color: 'text.feedback.success',
      },
      behind: {
        borderColor: 'border.feedback.warning.secondary',
        background: 'background.feedback.warning.primary.alpha',
        color: 'text.feedback.warning',
      },
    },
  },
  defaultVariants: {
    tone: 'ahead',
  },
});
